import React, { useContext, useEffect, useState } from 'react';
import {
	checkValidation,
	getHelperText,
	getLabelValue,
	ICancelInfo,
	ICountry,
	ITypeVignettesOrderManagementFields,
	IValidatedState,
	OfferContext,
	ValidatedState,
	VignetteContext,
} from '@adac-mp/shared';
import { Typography, Input, RadioButtons, PlatesInput } from '../../index';
import { useValidatedStates } from '../../hooks/useValidatedStates';

interface ICancelOrderFormProps {
	validateFields: boolean;
	countries: ICountry[];
	setHasError: (hasError: boolean) => void;
	updateCancelForm: (cancelInfo: ICancelInfo) => void;
}

export const CancelOrderForm = ({
	validateFields,
	countries,
	setHasError,
	updateCancelForm,
}: ICancelOrderFormProps) => {
	const { contentfulLabels } = useContext(VignetteContext);
	const { labels } = useContext(OfferContext);
	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;
	const [country, setCountry] = useState<ICountry | undefined>(countries[0]);
	const [inputType, setInputType] = useState<string>('orderNumber');
	const fields = ['orderNumber', 'plates', 'email'];

	const stateInit = {};
	fields.forEach((field) => {
		stateInit[field] = {
			value: field === 'plates' ? ['', '', ''] : undefined,
			error: false,
			warning: false,
			required: true,
		};
	});
	const [state, { updateWithValidation, update, updateWithError }] = useValidatedStates(stateInit);

	const checkForError = (singleState: IValidatedState) => {
		if (singleState.required) {
			return (
				(Array.isArray(singleState.value)
					? !singleState.value.every((val) => val === '')
					: singleState.value) && !singleState.error
			);
		}
		return false;
	};

	useEffect(() => {
		const allFilled =
			checkForError(state.email) && (checkForError(state.plates) || checkForError(state.orderNumber));
		if (allFilled) {
			updateCancelForm({
				email: state.email.value,
				orderNo: inputType === 'orderNumber' ? state.orderNumber.value : undefined,
				licenseplate: inputType === 'plates' ? state.plates.value.join('-') : undefined,
			});
		}
		setHasError(!allFilled);
	}, [state]);

	useEffect(() => {
		if (validateFields) {
			const currStates: [string, ValidatedState][] = Object.entries(state);
			currStates.forEach(([id, currState]) => {
				const checkValue = Array.isArray(currState.value)
					? currState.value.every((val) => val === '')
					: !currState.value;
				updateWithError(id, currState.value, checkValue || currState.error);
			});
		}
	}, [validateFields]);

	return (
		<>
			<Typography component="p" className="mb-3 pl-3">
				{currentLabels?.subheadlineIntro || 'Ihre Buchungsdaten'}
			</Typography>
			<RadioButtons
				helperTextStyle={{ marginTop: '10px', gap: '12px', paddingInline: '16px', paddingBottom: '8px' }}
				wrapperClassName="flex bg-white rounded mb-1.5"
				className="w-1/2 pl-4 border-r border-solid border-y-0 border-l-0 border-r-gray-600 py-[14px] md:py-3 last:border-r-0 cursor-pointer"
				labelClassName="w-full"
				value={inputType}
				setValue={(val) => {
					setInputType(val);
				}}
				options={[
					['orderNumber', currentLabels?.formData?.orderNumber || 'Bestellnummer'],
					['plates', getLabelValue('mp.licensePlate', labels) || 'Kennzeichen'],
				]}
			/>
			{inputType === 'orderNumber' ? (
				<Input
					placeholder={currentLabels?.formData?.orderNumber || 'Bestellnummer'}
					name={currentLabels?.formData?.orderNumber || 'Bestellnummer'}
					type="text"
					pattern="[0-9]*"
					inputMode="numeric"
					ariaLabel="Bestellnummer"
					autocomplete={currentLabels?.formData?.orderNumber || 'Bestellnummer'}
					value={state.orderNumber.value}
					hasError={state.orderNumber.error}
					helperText={currentLabels?.formData?.orderNumberError || getHelperText('NUMBER')}
					onBlur={(e) => {
						updateWithValidation('orderNumber', e.target.value, () =>
							checkValidation(e.target.value, 'NUMBER'),
						);
					}}
					onChange={(e) => {
						update('orderNumber', e.target.value);
					}}
				/>
			) : (
				<div className="mb-1.5">
					<PlatesInput
						countries={countries || []}
						plates={state.plates}
						country={country}
						setCountry={setCountry}
						onChange={({ value, error }) => {
							if (value) {
								updateWithError('plates', value, error);
							}
						}}
					/>
				</div>
			)}
			<Input
				placeholder={currentLabels?.formData?.email || 'Email'}
				type="email"
				autocomplete="email"
				name="email"
				ariaLabel="E-Mail-Adresse"
				value={state.email.value}
				hasError={state.email.error}
				helperText={currentLabels?.formData?.emailError || getHelperText('EMAIL')}
				onBlur={(e) => {
					updateWithValidation('email', e.target.value, () => checkValidation(e.target.value, 'EMAIL'));
				}}
				onChange={(e) => {
					update('email', e.target.value);
				}}
			/>
		</>
	);
};
